import './RichTextCustom.css';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';

import { useCallback, useMemo, useRef } from 'react';
import type { FieldValues, Path, Control, RegisterOptions } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import type { Rule } from 'utils/validations/plainTextLengthValidator';
import { plainTextLengthValidator } from 'utils/validations/plainTextLengthValidator';

interface RichTextProps<T extends FieldValues> {
  control: Control<T>;
  registerName: Path<T>;
  label?: string;
  isDisabled?: boolean;
  registerProps?: RegisterOptions<T, Path<T>>;
  error?: FieldValues['errors'];
  whisper?: string[];
  whisperLabel?: string;
  detail?: boolean;
  onBlur?: () => void;
}

/**
 * @deprecated
 */
export const RichText = <T extends FieldValues>({
  label,
  control,
  registerName,
  isDisabled,
  registerProps,
  error,
  whisper,
  whisperLabel,
  detail = false,
  onBlur,
}: RichTextProps<T>) => {
  const quillRef = useRef<ReactQuill>(null);

  const handleWhisperSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const quill = quillRef.current?.getEditor();
      const range = quill?.getSelection();
      if (quill && range) {
        quill.insertText(range.index, '$' + event.target.value);
      }
    },
    [quillRef]
  );

  const rules = useMemo(() => {
    const { maxLength, ...rest } = registerProps || {};
    return {
      ...rest,
      validate: plainTextLengthValidator({ maxLength } as Rule),
    };
  }, [registerProps]);

  return (
    <div>
      <div className="flex justify-between">
        {label && (
          <label htmlFor={registerName} className="mb-1 text-sm">
            {label}
          </label>
        )}
        {whisper && (
          <select className="text-sm bg-transparent focus:outline-none" value="" onChange={handleWhisperSelect}>
            <option disabled value="">
              {whisperLabel}
            </option>
            {whisper.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>
      <Controller
        name={registerName}
        control={control}
        disabled={isDisabled}
        rules={rules}
        render={({ field }) => (
          <div className="flex flex-col">
            <ReactQuill
              {...field}
              ref={quillRef}
              theme={detail ? 'bubble' : 'snow'}
              defaultValue={field.value}
              readOnly={isDisabled}
              placeholder="..."
              style={error && { border: '1px solid red' }}
              modules={{
                toolbar: [
                  [{ header: '1' }, { header: '2' }],
                  [{ font: [] }],
                  [{ size: [] }],
                  [{ align: [] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ color: [] }, { background: [] }],
                  [{ script: 'sub' }, { script: 'super' }],
                  ['blockquote', 'code-block'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link', 'image'],
                ],
              }}
              onBlur={() => {
                field.onBlur();
                onBlur?.();
              }}
            />
          </div>
        )}
      />
      {error && <span className="text-xs text-red-500"> {error.message}</span>}
    </div>
  );
};
